import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Stack,
} from '@mui/material';
import { Link } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';
import { InventorySyncEventItem } from '@domain/connection/models/InventorySyncEvent';

interface InventoryPreviewProps {
    inventory?: InventorySyncEventItem[];
    isLoading?: boolean;
    emptyMessage?: string;
    showEditButton?: boolean;
    onEditMapping?: () => void;
    title?: string;
}

export const InventoryPreview: React.FC<InventoryPreviewProps> = ({
    inventory,
    isLoading = false,
    emptyMessage = 'No inventory to show',
    showEditButton = false,
    onEditMapping,
    title = 'Preview of your inventory',
}) => {
    const [itemsToShow, setItemsToShow] = useState(10);

    return (
        <Box
            sx={{
                backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint,
                border: '1px solid',
                borderColor: APP_CONFIG.ui.theme.colors.border,
                borderRadius: 1,
            }}
        >
            {inventory && inventory.length > 0 ? (
                <>
                    <Typography p={2} variant="subtitle2" color="text.primary">
                        {title}
                    </Typography>

                    {isLoading ? (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>EAN</TableCell>
                                            <TableCell align="right" width="100px">Quantity</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {inventory
                                            .slice(0, itemsToShow)
                                            .map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell sx={{ maxWidth: 0, width: '70%' }}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.primary"
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    minWidth: 0
                                                                }}
                                                            >
                                                                {item.ean}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ width: '30%' }}>
                                                        {item.quantity}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {inventory.length > itemsToShow && (
                                <Box sx={{ p: 1 }}>
                                    <Button
                                        variant="text"
                                        onClick={() => setItemsToShow(prev => Math.min(prev + 10, inventory.length))}
                                        fullWidth
                                    >
                                        Show more items
                                    </Button>
                                </Box>
                            )}

                            {showEditButton && onEditMapping && (
                                <Button
                                    variant="text"
                                    onClick={onEditMapping}
                                    fullWidth
                                    startIcon={<Link sx={{ fontSize: 16 }} />}
                                    sx={{ p: 2 }}
                                >
                                    <Typography variant="subtitle2" color="text.primary">
                                        Edit mapping
                                    </Typography>
                                </Button>
                            )}
                        </>
                    )}
                </>
            ) : (
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {emptyMessage}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

